import React, { useCallback, useContext, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generatePath, Navigate, NavLink, useSearchParams } from "react-router-dom";
import { useTracking } from "react-tracking";

import { Input } from "@airhelp/react";
import { Box, Button, Heading, Link, Text } from "@chakra-ui/react";
import { HTTPError } from "ky";

import { handleErrorMessage, signupUser } from "api/user/signup";
import membershipActivationImage from "assets/images/membership-activation@1x.jpg";
import { TwoColumnsImageLayout } from "components/Layouts";
import { UNKNOWN_CHANNEL } from "config/channels";
import { PAGE_PATHS } from "config/routes";
import { SessionContext, TrackingContext } from "contexts";
import { ErrorBadge } from "elements";
import UserConsentsForm from "elements/UserConsentsForm";
import { Offer } from "models/Offers";
import { ISignupSubmitData, IUser } from "models/User";
import randomPassword from "utils/generateRandomPassword";
import { emailPattern } from "utils/validations";

import { getMembershipPlanByChannel, isCompleteAnnualMembership } from "../membershipHelper";

interface IComponent {
  offers: Record<string, Offer>;
  renewOffer?: Offer;
  summerTimeEnabled: boolean;
}

const EmailView: React.FC<IComponent> = ({ offers, renewOffer, summerTimeEnabled }) => {
  const { login } = useContext(SessionContext);
  const { setLoginData } = useContext(TrackingContext);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { trackEvent } = useTracking();

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>();

  const form = useForm<ISignupSubmitData>({ mode: "onChange" });
  const { handleSubmit, control, formState } = form;

  const { isValid, errors } = formState;
  const isDisabled = isLoading || !isValid || !!apiError;

  const channel = (searchParams.get("channel") || UNKNOWN_CHANNEL) as string;

  const product = getMembershipPlanByChannel({ channel, renewOffer, offers, summerTimeEnabled });
  const isCompleteMembership = isCompleteAnnualMembership(product.name);

  const onSubmit = useCallback(
    async (data: ISignupSubmitData) => {
      trackEvent({
        name: "GAEvent",
        eventCategory: "ahplus",
        eventAction: "clicked",
        eventLabel: "ahplus_membership_buy_email_continue_clicked",
      });

      setApiError(null);
      setIsLoading(true);

      const { email, privacyConsent, marketingConsent } = data;
      const userData = {
        email,
        password: randomPassword,
        passwordConfirmation: randomPassword,
        privacyConsent,
        marketingConsent,
      };
      const [error, response] = await signupUser(userData);
      const errorResponse = error as HTTPError;
      const session = error ? null : response.user;

      if (!session) {
        const errorMessage = await handleErrorMessage(errorResponse, t);
        setApiError(errorMessage);
        setIsLoading(false);
      } else {
        setLoginData({ method: "email", user: session as IUser });
        login(session as IUser);
        setIsLoading(false);

        return (
          <Navigate
            to={{ pathname: generatePath(PAGE_PATHS.MEMBERSHIPS.BUY.CHECKOUT_PAGE), search: searchParams.toString() }}
          />
        );
      }
    },
    [setIsLoading, setApiError]
  );

  return (
    <TwoColumnsImageLayout imageSrc={membershipActivationImage}>
      <Box width="100%">
        <Heading variant="h1" mt={{ base: 0, lg: 8 }}>
          {t("common.get_airhelp_plus")}
        </Heading>
        <Text fontSize="lg" fontWeight="medium" mt={3}>
          {t("memberships.we_will_send_confirmation_to_this_email")}
        </Text>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box mt={8} mb={2} width="100%" minHeight="106px" data-testid="membership-email-form-control">
            <Controller
              name="email"
              control={control}
              rules={{
                required: { value: true, message: t("errors.required") },
                pattern: { value: emailPattern, message: t("errors.email_invalid") },
              }}
              defaultValue=""
              render={({ field: { onChange, name, value } }) => (
                <Input
                  isInvalid={!!errors.email}
                  errorMessage={errors.email?.message || ""}
                  placeholder={t("common.email_placeholder")}
                  size="lg"
                  label={t("common.type_your_email")}
                  name={name}
                  type="email"
                  value={value}
                  id="email"
                  onChange={(e) => {
                    onChange(e);
                    setApiError(null);
                  }}
                  data-testid="membership-email"
                />
              )}
            />
          </Box>
          {apiError && (
            <Box mt={-4} pb={4}>
              <ErrorBadge data-testid="membership-api-error">{t("common.account_already_exist")}</ErrorBadge>
              <Link as={NavLink} to={generatePath(PAGE_PATHS.LOGIN_PAGE)}>
                <Text fontSize="sm" color="primary.500" mt={2}>
                  {t("common.please_log_in_here")}
                </Text>
              </Link>
            </Box>
          )}
          <UserConsentsForm form={form} isCompleteMembership={isCompleteMembership} requireTerms requireMarketing />
          <Button
            mt={6}
            size="s"
            width="100%"
            onClick={handleSubmit(onSubmit)}
            isDisabled={isDisabled}
            isLoading={isLoading}
            data-testid="membership-create-account-button"
          >
            {t("common.continue")}
          </Button>
        </form>
      </Box>
    </TwoColumnsImageLayout>
  );
};

export default EmailView;
